// src/beta/components/MatchingTable.tsx
import type { V1MatchingComponent } from '@/beta/types/dossier';

interface Props {
  components: V1MatchingComponent[];
}

const STATUS_COLOR: Record<V1MatchingComponent['status'], string> = {
  matching: 'var(--vm-red)',
  'non-matching': 'var(--vm-stone-light)',
  unverified: 'var(--vm-stone)',
};

function StatusPill({ c }: { c: V1MatchingComponent }) {
  return (
    <span
      data-testid={`status-pill-${c.id}`}
      data-status={c.status}
      style={{
        color: STATUS_COLOR[c.status],
        border: `1px solid ${STATUS_COLOR[c.status]}`,
        padding: '2px 10px',
        fontFamily: 'var(--vm-font-heading)',
        fontSize: 10,
        letterSpacing: '0.3em',
        textTransform: 'uppercase',
      }}
    >
      {c.status}
    </span>
  );
}

function citationsLabel(n: number): string {
  if (n === 0) return 'no citations';
  if (n === 1) return '1 citation';
  return `${n} citations`;
}

/** § 6.4 (5): Matching components table.
 *  Columns: kind / value / status pill / citation count. Status pill colour
 *  changes by status (matching = red, non-matching = stone-light, unverified = stone). */
export function MatchingTable({ components }: Props) {
  if (components.length === 0) {
    return (
      <p style={{ color: 'var(--vm-stone)', fontSize: 13, fontFamily: 'var(--vm-font-body)' }}>
        No matching-components on file.
      </p>
    );
  }

  return (
    <table
      style={{
        width: '100%',
        borderCollapse: 'collapse',
        fontFamily: 'var(--vm-font-body)',
        color: 'var(--vm-warm-white)',
        fontSize: 13,
      }}
    >
      <thead>
        <tr>
          {['Kind', 'Value', 'Status', 'Citations'].map((h) => (
            <th
              key={h}
              style={{
                textAlign: 'left',
                padding: 'var(--vm-s-1) 0',
                fontFamily: 'var(--vm-font-heading)',
                fontSize: 10,
                letterSpacing: '0.3em',
                textTransform: 'uppercase',
                color: 'var(--vm-stone)',
                borderBottom: '1px solid var(--vm-border)',
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {components.map((c) => (
          <tr key={c.id} style={{ borderBottom: '1px solid var(--vm-border)' }}>
            <td style={{ padding: 'var(--vm-s-2) 0', textTransform: 'capitalize' }}>{c.kind}</td>
            <td style={{ padding: 'var(--vm-s-2) 0', fontFamily: 'monospace' }}>
              {c.value ?? '—'}
            </td>
            <td style={{ padding: 'var(--vm-s-2) 0' }}>
              <StatusPill c={c} />
            </td>
            <td style={{ padding: 'var(--vm-s-2) 0', color: 'var(--vm-stone)' }}>
              {citationsLabel(c.citation_count)}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
